import React, { useId, useState } from 'react';
import { type NativeUserPickerProps } from '@atlassian/forge-ui-types';

import AKUserPicker from '@atlaskit/user-picker';
import AKSmartUserPicker from '@atlaskit/smart-user-picker';
import { HelperMessage as AKHelperMessage } from '@atlaskit/form/helper-message';

import { PortalConsumer } from '../../../context';

import { type UserPickerOption } from '../../UIKit1/form/transformFormData';
import { type UserQuery } from '../../UIKit1/userPicker/providers/useMentionResource';
import type { Dependencies } from './types';
import { Label as UIKitLabel } from '../label';

const BITBUCKET_FIELD_ID = 'BitbucketMentions';

type InnerUserPickerProps = Omit<NativeUserPickerProps, 'defaultValue'> &
	Omit<Dependencies, 'client'> & {
		defaultValue?: UserPickerOption | UserPickerOption[];
		id: string;
		query?: UserQuery;
		width: string;
	};

// the value prop in UserPicker component can only take undefined for empty value.
// null is not allowed and will resulted in unexpected behavior.
const ensureUserPickerValue: <T>(value: T) => T | undefined = (value) => {
	if (!value || (Array.isArray(value) && value.length === 0)) {
		return undefined;
	}
	return value;
};

// by forcing a key update when defaultValue is present, we force the SmartUserPicker to re-render.
// this is because the SmartUserPicker does not re-render when the defaultValue changes in its
// current implementation.
const makeUserPickerKey = (id: string, defaultValue?: UserPickerOption | UserPickerOption[]) => {
	if (!defaultValue || (Array.isArray(defaultValue) && defaultValue.length === 0)) {
		return id;
	}
	return `${id}-with-default`;
};

const RawUserPicker = ({
	id,
	name,
	description,
	placeholder,
	isMulti,
	includeUsers = true,
	includeGroups = false,
	accountId: currentAccountId,
	cloudId: suppliedCloudId,
	productKey,
	baseUrl,
	onChange,
	query,
	defaultValue,
	value: providedValue,
	width,
	productAttributes,
}: InnerUserPickerProps & {
	value?: UserPickerOption | UserPickerOption[];
}) => {
	const [value, setValue] = useState<any>(defaultValue);

	return (
		<PortalConsumer>
			{(portal) => (
				<>
					{suppliedCloudId && productKey ? (
						<AKSmartUserPicker
							key={makeUserPickerKey(id, defaultValue)}
							onChange={(user: any) => onChange?.(user)}
							isMulti={isMulti}
							menuPortalTarget={portal || undefined}
							noOptionsMessage={() => null}
							placeholder={placeholder}
							width={width}
							fieldId={
								// Bitbucket User Recommendations does not support generic contextType
								productKey === 'bitbucket' ? BITBUCKET_FIELD_ID : id
							}
							debounceTime={400}
							includeUsers={includeUsers}
							includeGroups={includeGroups}
							siteId={suppliedCloudId}
							productAttributes={productAttributes}
							// If principalId === 'Context', upstream attempts to use other context
							// to discover the principal ID
							principalId={currentAccountId ?? 'Context'}
							productKey={productKey}
							baseUrl={baseUrl}
							inputId={name || id}
							defaultValue={defaultValue}
							// Override the default height of user picker so that it aligns with other input components from design system
							styles={{
								control: (base) => ({
									...base,
									minHeight: '40px',
									height: '40px',
								}),
							}}
						/>
					) : (
						<AKUserPicker
							value={ensureUserPickerValue(providedValue ?? value)}
							onChange={(user: any) => {
								setValue(user);
								onChange?.(user);
							}}
							// Override the default height of user picker so that it aligns with other input components from design system
							styles={{
								control: (base) => ({
									...base,
									minHeight: '40px',
									height: '40px',
								}),
							}}
							isMulti={isMulti}
							menuPortalTarget={portal || undefined}
							noOptionsMessage={() => null}
							placeholder={placeholder}
							width={width}
							loadOptions={query}
							fieldId={null}
							inputId={id}
							defaultValue={defaultValue}
						/>
					)}
					{description && <AKHelperMessage>{description}</AKHelperMessage>}
				</>
			)}
		</PortalConsumer>
	);
};

// This is a wrapper containing the core logic of user picker
// It has been separated to take query as props so that it can be testable in Jest and rendered in Storybook
export const UserPicker = (
	props: Omit<InnerUserPickerProps, 'id' | 'width'>,
): React.JSX.Element => {
	const uid = useId();
	const { name, isRequired, label } = props;
	const id = name || uid;

	return (
		<>
			{/* Must be named UI Kit Label to avoid triggering ratchetting rules */}
			{label && <UIKitLabel name={name} isRequired={isRequired} label={label} />}
			<RawUserPicker id={id} width="100%" {...props} />
		</>
	);
};
